import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, IconButton, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import confetti from 'canvas-confetti';
import { post } from '../../utils/apiClient';
import { logger } from '../../utils/logger';

interface Milestone {
  type: string;
  key: string;
  message: string;
  title?: string;
  icon?: string;
  count?: number;
}

interface MicroCelebrationProps {
  milestones: { milestones: Milestone[] } | null;
  streakMilestone: {
    days: number;
    message: string;
    type: string;
  } | null;
}

const ICON_MAP: Record<string, string> = {
  sparkles: '\u2728',
  car: '\uD83D\uDE97',
  microphone: '\uD83C\uDFA4',
  lightbulb: '\uD83D\uDCA1',
  clipboard: '\uD83D\uDCCB',
  trophy: '\uD83C\uDFC6',
  fire: '\uD83D\uDD25',
};

function fireConfetti() {
  try {
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { y: 0.7 },
      colors: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'],
      disableForReducedMotion: true,
    });
  } catch (err) {
    logger.warn('Confetti animation failed:', err);
  }
}

/**
 * Micro-celebration overlay component.
 * Shows a brief celebration toast with confetti for milestones like:
 * - First-use milestones (first AI note, first Drive Mode, etc.)
 * - Note count milestones (10, 25, 50, 100, etc.)
 * - Streak milestones (7, 14, 30, 60, 90 days)
 */
function MicroCelebration({ milestones, streakMilestone }: MicroCelebrationProps) {
  const [visible, setVisible] = useState(false);
  const [currentCelebration, setCurrentCelebration] = useState<{
    title: string;
    message: string;
    icon: string;
    key: string;
    type: string;
  } | null>(null);

  const dismissCelebration = useCallback(async () => {
    if (!currentCelebration) return;
    setVisible(false);

    try {
      if (currentCelebration.type === 'streak_milestone') {
        await post(
          `/api/therapist_dashboard/briefing/streak-milestone?milestone_days=${encodeURIComponent(currentCelebration.key)}`
        );
      } else {
        await post(
          `/api/therapist_dashboard/briefing/dismiss-milestone?milestone_key=${encodeURIComponent(currentCelebration.key)}`
        );
      }
    } catch (err) {
      logger.warn('Failed to dismiss milestone:', err);
    }
  }, [currentCelebration]);

  useEffect(() => {
    // Check for streak milestone first
    if (streakMilestone) {
      setCurrentCelebration({
        title: `${streakMilestone.days}-Day Streak!`,
        message: streakMilestone.message,
        icon: ICON_MAP.fire || '\uD83D\uDD25',
        key: String(streakMilestone.days),
        type: 'streak_milestone',
      });
      setVisible(true);
      fireConfetti();
      return;
    }

    // Check for other milestones
    const celebrationList = milestones?.milestones || [];
    if (celebrationList.length > 0) {
      const first = celebrationList[0];
      setCurrentCelebration({
        title: first.title || 'Milestone!',
        message: first.message,
        icon: ICON_MAP[first.icon || 'trophy'] || '\uD83C\uDFC6',
        key: first.key,
        type: first.type,
      });
      setVisible(true);
      fireConfetti();
    }
  }, [milestones, streakMilestone]);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => {
      dismissCelebration();
    }, 8000);
    return () => clearTimeout(timer);
  }, [visible, dismissCelebration]);

  if (!visible || !currentCelebration) {
    return null;
  }

  return (
    <Slide direction="down" in={visible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 72,
          left: 0,
          right: 0,
          mx: 'auto',
          zIndex: 9999,
          maxWidth: 400,
          width: '90%',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 6,
          border: 1,
          borderColor: 'primary.light',
          overflow: 'hidden',
        }}
      >
        {/* Accent bar */}
        <Box
          sx={{
            height: 4,
            background: 'linear-gradient(90deg, #FF6B6B, #4ECDC4, #45B7D1, #FFA07A)',
          }}
        />
        <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Typography sx={{ fontSize: 28, lineHeight: 1 }}>
            {currentCelebration.icon}
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {currentCelebration.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {currentCelebration.message}
            </Typography>
          </Box>
          <IconButton size="small" onClick={dismissCelebration} sx={{ mt: -0.5 }} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Slide>
  );
}

export default MicroCelebration;
